// @core/stores/document/DocumentStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { documentApi } from '@core/api';
import type {
  AcknowledgeDocumentRequest,
  AcquireDocumentLockRequest,
  AppendBlocksRequest,
  AppendBlocksResponse,
  ApproveClauseRequest,
  ArchiveDocumentRequest,
  AssignDocumentNumberRequest,
  BatchUpdateBlocksRequest,
  BatchUpdateBlocksResponse,
  BlockResponse,
  BuildTemplateFromClausesRequest,
  CancelPipelineExecutionRequest,
  CategoryResponse,
  CreateCategoryRequest,
  CreateClauseRequest,
  CreateDocumentFromTemplateRequest,
  CreateDocumentPolicyRequest,
  CreateDocumentRegisterRequest,
  CreateDocumentRequest,
  CreateDocumentTemplateRequest,
  CreateNumberingSchemeRequest,
  CreatePipelineRequest,
  CreateRevisionRequest,
  DelegateStageActionRequest,
  DeprecateClauseRequest,
  Document,
  DocumentClauseResponse,
  DocumentLifecycleResponse,
  DocumentLockResponse,
  DocumentPolicyResponse,
  DocumentRegisterResponse,
  DocumentRegistrationResponse,
  DocumentResponse,
  DocumentStatsResponse,
  DocumentTemplateResponse,
  ExportDocumentRequest,
  ExportDocumentResponse,
  GetBlockChildrenResponse,
  GetDocumentContentResponse,
  InstantiateDocumentFromTemplateRequest,
  ListAcknowledgmentsResponse,
  ListAuditLogResponse,
  ListCategoriesResponse,
  ListClausesResponse,
  ListDocumentPoliciesResponse,
  ListDocumentRegistersResponse,
  ListDocumentRegistrationsResponse,
  ListDocumentTemplatesResponse,
  ListNumberingCountersResponse,
  ListNumberingSchemesResponse,
  ListPipelineExecutionsResponse,
  ListPipelinesResponse,
  ListVersionsResponse,
  MoveBlockRequest,
  NumberingSchemeResponse,
  PipelineExecutionResponse,
  PipelineResponse,
  PreviewDocumentNumberRequest,
  PreviewDocumentNumberResponse,
  ProcessStageActionRequest,
  PublishDocumentRequest,
  RegisterDocumentRequest,
  RequestAcknowledgmentRequest,
  ResetNumberingCounterRequest,
  ResolveDocumentContextRequest,
  ResolveDocumentContextResponse,
  SearchDocumentsResponse,
  StartPipelineRequest,
  SubmitForApprovalRequest,
  UpdateBlockRequest,
  UpdateCategoryRequest,
  UpdateClauseRequest,
  UpdateDocumentPolicyRequest,
  UpdateDocumentRequest,
  UpdateDocumentTemplateRequest,
  UpdateNumberingSchemeRequest,
  UpdatePipelineRequest,
  VersionResponse,
} from '@core/types/document';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class DocumentStore {
  // List state
  items: Document[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Document | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Document>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Document | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Document | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Acknowledge document
   */
  async AcknowledgeDocument(target_type: string, target_id: string, data: AcknowledgeDocumentRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.AcknowledgeDocument(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Acquire document lock
   */
  async AcquireDocumentLock(document_id: string, data: AcquireDocumentLockRequest): Promise<DocumentLockResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.AcquireDocumentLock(document_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Append blocks
   */
  async AppendBlocks(document_id: string, data: AppendBlocksRequest): Promise<AppendBlocksResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.AppendBlocks(document_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Approve clause
   */
  async ApproveClause(clause_id: string, data: ApproveClauseRequest): Promise<DocumentClauseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ApproveClause(clause_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Archive document
   */
  async ArchiveDocument(target_type: string, target_id: string, data: ArchiveDocumentRequest): Promise<DocumentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ArchiveDocument(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Assign document number
   */
  async AssignDocumentNumber(target_type: string, target_id: string, data: AssignDocumentNumberRequest): Promise<DocumentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.AssignDocumentNumber(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Batch update blocks
   */
  async BatchUpdateBlocks(document_id: string, data: BatchUpdateBlocksRequest): Promise<BatchUpdateBlocksResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.BatchUpdateBlocks(document_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Build template from clauses
   */
  async BuildTemplateFromClauses(data: BuildTemplateFromClausesRequest): Promise<DocumentTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.BuildTemplateFromClauses(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel pipeline
   */
  async CancelPipelineExecution(target_type: string, target_id: string, execution_id: string, data: CancelPipelineExecutionRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CancelPipelineExecution(target_type, target_id, execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create category
   */
  async CreateCategory(data: CreateCategoryRequest): Promise<CategoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateCategory(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create clause
   */
  async CreateClause(data: CreateClauseRequest): Promise<DocumentClauseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateClause(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create document
   */
  async CreateDocument(data: CreateDocumentRequest): Promise<Document> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await documentApi.CreateDocument(data);
      const item = response.document;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create from template
   */
  async CreateDocumentFromTemplate(template_id: string, data: CreateDocumentFromTemplateRequest): Promise<Document> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await documentApi.CreateDocumentFromTemplate(template_id, data);
      const item = response.document;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create policy
   */
  async CreateDocumentPolicy(data: CreateDocumentPolicyRequest): Promise<DocumentPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateDocumentPolicy(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create register
   */
  async CreateDocumentRegister(data: CreateDocumentRegisterRequest): Promise<DocumentRegisterResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateDocumentRegister(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create template
   */
  async CreateDocumentTemplate(data: CreateDocumentTemplateRequest): Promise<DocumentTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateDocumentTemplate(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create numbering scheme
   */
  async CreateNumberingScheme(data: CreateNumberingSchemeRequest): Promise<NumberingSchemeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateNumberingScheme(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create pipeline
   */
  async CreatePipeline(data: CreatePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreatePipeline(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create revision
   */
  async CreateRevision(target_type: string, target_id: string, data: CreateRevisionRequest): Promise<DocumentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.CreateRevision(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delegate stage action
   */
  async DelegateStageAction(target_type: string, target_id: string, execution_id: string, data: DelegateStageActionRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.DelegateStageAction(target_type, target_id, execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete block
   */
  async DeleteBlock(document_id: string, block_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteBlock(document_id, block_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== block_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === block_id) {
          this.currentItem = null;
        }

        this.removeFromCache(block_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete category
   */
  async DeleteCategory(category_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteCategory(category_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== category_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === category_id) {
          this.currentItem = null;
        }

        this.removeFromCache(category_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete clause
   */
  async DeleteClause(clause_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteClause(clause_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== clause_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === clause_id) {
          this.currentItem = null;
        }

        this.removeFromCache(clause_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete document
   */
  async DeleteDocument(document_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteDocument(document_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== document_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === document_id) {
          this.currentItem = null;
        }

        this.removeFromCache(document_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete policy
   */
  async DeleteDocumentPolicy(policy_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteDocumentPolicy(policy_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_id) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete template
   */
  async DeleteDocumentTemplate(template_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteDocumentTemplate(template_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== template_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === template_id) {
          this.currentItem = null;
        }

        this.removeFromCache(template_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete numbering scheme
   */
  async DeleteNumberingScheme(scheme_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeleteNumberingScheme(scheme_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== scheme_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === scheme_id) {
          this.currentItem = null;
        }

        this.removeFromCache(scheme_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete pipeline
   */
  async DeletePipeline(pipeline_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await documentApi.DeletePipeline(pipeline_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== pipeline_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === pipeline_id) {
          this.currentItem = null;
        }

        this.removeFromCache(pipeline_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Deprecate clause
   */
  async DeprecateClause(clause_id: string, data: DeprecateClauseRequest): Promise<DocumentClauseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.DeprecateClause(clause_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Export document
   */
  async ExportDocument(document_id: string, data: ExportDocumentRequest): Promise<ExportDocumentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ExportDocument(document_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Find templates for entity
   */
  async FindTemplatesForEntity(entity_type: string, entity_id: string): Promise<ListDocumentTemplatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.FindTemplatesForEntity(entity_type, entity_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get block children
   */
  async GetBlockChildren(document_id: string, block_id: string): Promise<GetBlockChildrenResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetBlockChildren(document_id, block_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get clause
   */
  async GetClause(clause_id: string): Promise<DocumentClauseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetClause(clause_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get document
   */
  async GetDocument(document_id: string): Promise<Document> {
    // Check cache first
    const cached = this.cache.get(document_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await documentApi.GetDocument(document_id);
      const item = response.document;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get document content
   */
  async GetDocumentContent(document_id: string): Promise<GetDocumentContentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetDocumentContent(document_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get policy
   */
  async GetDocumentPolicy(policy_id: string): Promise<DocumentPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetDocumentPolicy(policy_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get entity policy
   */
  async GetDocumentPolicyByEntity(entity_type: string, entity_id: string): Promise<DocumentPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetDocumentPolicyByEntity(entity_type, entity_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get document statistics
   */
  async GetDocumentStats(params?: { }): Promise<DocumentStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetDocumentStats(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get template
   */
  async GetDocumentTemplate(template_id: string): Promise<DocumentTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetDocumentTemplate(template_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get numbering scheme
   */
  async GetNumberingScheme(scheme_id: string): Promise<NumberingSchemeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetNumberingScheme(scheme_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get pipeline
   */
  async GetPipeline(pipeline_id: string): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetPipeline(pipeline_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get pipeline execution
   */
  async GetPipelineExecution(target_type: string, target_id: string, execution_id: string): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetPipelineExecution(target_type, target_id, execution_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get version
   */
  async GetVersion(target_type: string, target_id: string, version_id: string): Promise<VersionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.GetVersion(target_type, target_id, version_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Instantiate document from template
   */
  async InstantiateDocumentFromTemplate(template_id: string, data: InstantiateDocumentFromTemplateRequest): Promise<DocumentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.InstantiateDocumentFromTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List acknowledgments
   */
  async ListAcknowledgments(target_type: string, target_id: string, params?: PaginatedRequest): Promise<ListAcknowledgmentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListAcknowledgments(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List audit log
   */
  async ListAuditLog(target_type: string, target_id: string, params?: PaginatedRequest): Promise<ListAuditLogResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListAuditLog(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List categories
   */
  async ListCategories(params?: PaginatedRequest): Promise<ListCategoriesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListCategories(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List clauses
   */
  async ListClauses(params?: PaginatedRequest): Promise<ListClausesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListClauses(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List policies
   */
  async ListDocumentPolicies(params?: PaginatedRequest): Promise<ListDocumentPoliciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListDocumentPolicies(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List registers
   */
  async ListDocumentRegisters(params?: PaginatedRequest): Promise<ListDocumentRegistersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListDocumentRegisters(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List registrations
   */
  async ListDocumentRegistrations(register_id: string, params?: PaginatedRequest): Promise<ListDocumentRegistrationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListDocumentRegistrations(register_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List templates
   */
  async ListDocumentTemplates(params?: PaginatedRequest): Promise<ListDocumentTemplatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListDocumentTemplates(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List documents
   */
  async ListDocuments(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await documentApi.ListDocuments(params);

      runInAction(() => {
        this.items = response.documents?.documents ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List counters
   */
  async ListNumberingCounters(scheme_id: string, params?: PaginatedRequest): Promise<ListNumberingCountersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListNumberingCounters(scheme_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List numbering schemes
   */
  async ListNumberingSchemes(params?: PaginatedRequest): Promise<ListNumberingSchemesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListNumberingSchemes(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipeline executions
   */
  async ListPipelineExecutions(target_type: string, target_id: string, params?: PaginatedRequest): Promise<ListPipelineExecutionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListPipelineExecutions(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipelines
   */
  async ListPipelines(params?: PaginatedRequest): Promise<ListPipelinesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListPipelines(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List versions
   */
  async ListVersions(target_type: string, target_id: string, params?: PaginatedRequest): Promise<ListVersionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ListVersions(target_type, target_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Move block
   */
  async MoveBlock(document_id: string, block_id: string, data: MoveBlockRequest): Promise<BlockResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.MoveBlock(document_id, block_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Preview next number
   */
  async PreviewDocumentNumber(scheme_id: string, data: PreviewDocumentNumberRequest): Promise<PreviewDocumentNumberResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.PreviewDocumentNumber(scheme_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Process stage action
   */
  async ProcessStageAction(target_type: string, target_id: string, execution_id: string, data: ProcessStageActionRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ProcessStageAction(target_type, target_id, execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Publish document
   */
  async PublishDocument(target_type: string, target_id: string, data: PublishDocumentRequest): Promise<DocumentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.PublishDocument(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register document
   */
  async RegisterDocument(register_id: string, data: RegisterDocumentRequest): Promise<DocumentRegistrationResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.RegisterDocument(register_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Release document lock
   */
  async ReleaseDocumentLock(document_id: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ReleaseDocumentLock(document_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Request acknowledgment
   */
  async RequestAcknowledgment(target_type: string, target_id: string, data: RequestAcknowledgmentRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.RequestAcknowledgment(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reset counter
   */
  async ResetNumberingCounter(scheme_id: string, data: ResetNumberingCounterRequest): Promise<NumberingSchemeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ResetNumberingCounter(scheme_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve document context
   */
  async ResolveDocumentContext(data: ResolveDocumentContextRequest): Promise<ResolveDocumentContextResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.ResolveDocumentContext(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search documents
   */
  async SearchDocuments(params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<SearchDocumentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.SearchDocuments(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Start pipeline
   */
  async StartPipeline(target_type: string, target_id: string, pipeline_id: string, data: StartPipelineRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.StartPipeline(target_type, target_id, pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Submit for approval
   */
  async SubmitForApproval(target_type: string, target_id: string, data: SubmitForApprovalRequest): Promise<DocumentLifecycleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.SubmitForApproval(target_type, target_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update block
   */
  async UpdateBlock(document_id: string, block_id: string, data: UpdateBlockRequest): Promise<BlockResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdateBlock(document_id, block_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update category
   */
  async UpdateCategory(category_id: string, data: UpdateCategoryRequest): Promise<CategoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdateCategory(category_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update clause
   */
  async UpdateClause(clause_id: string, data: UpdateClauseRequest): Promise<DocumentClauseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdateClause(clause_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update document
   */
  async UpdateDocument(document_id: string, data: UpdateDocumentRequest): Promise<Document> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await documentApi.UpdateDocument(document_id, data);
      const item = response.document;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update policy
   */
  async UpdateDocumentPolicy(policy_id: string, data: UpdateDocumentPolicyRequest): Promise<DocumentPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdateDocumentPolicy(policy_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update template
   */
  async UpdateDocumentTemplate(template_id: string, data: UpdateDocumentTemplateRequest): Promise<DocumentTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdateDocumentTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update numbering scheme
   */
  async UpdateNumberingScheme(scheme_id: string, data: UpdateNumberingSchemeRequest): Promise<NumberingSchemeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdateNumberingScheme(scheme_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update pipeline
   */
  async UpdatePipeline(pipeline_id: string, data: UpdatePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await documentApi.UpdatePipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const documentStore = new DocumentStore();
